import { Injectable, signal } from '@angular/core';

export type AgeTier = 'primaire' | 'college' | 'lycee';

@Injectable({
  providedIn: 'root',
})
export class AgeTierService {
  private readonly STORAGE_KEY = 'age-tier';
  private readonly DEFAULT_TIER: AgeTier = 'college';

  readonly currentTier = signal<AgeTier>(this.DEFAULT_TIER);

  loadInitialTier(): void {
    const saved = localStorage.getItem(this.STORAGE_KEY) as AgeTier | null;
    const tier = this.isValid(saved) ? saved : this.DEFAULT_TIER;
    this.applyTier(tier);
  }

  setTier(tier: AgeTier): void {
    if (!this.isValid(tier)) return;
    this.applyTier(tier);
  }

  /**
   * Compute the age tier from a birth date (ISO `YYYY-MM-DD`).
   * Returns null when the date is missing or unparseable.
   *
   * Mapping (French school system):
   *   ≤ 10 ans → primaire (CP-CM2)
   *   11-14    → college  (6e-3e)
   *   ≥ 15     → lycee    (Seconde-Terminale)
   */
  tierFromBirthDate(birthDate: string | null | undefined): AgeTier | null {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    if (Number.isNaN(birth.getTime())) return null;

    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;

    if (age <= 10) return 'primaire';
    if (age <= 14) return 'college';
    return 'lycee';
  }

  /**
   * Derive the tier from `birthDate` and apply it. Returns the resolved tier
   * (or null if the birth date could not be used).
   */
  setTierFromBirthDate(birthDate: string | null | undefined): AgeTier | null {
    const derived = this.tierFromBirthDate(birthDate);
    if (derived) this.applyTier(derived);
    return derived;
  }

  private applyTier(tier: AgeTier): void {
    document.documentElement.setAttribute('data-tier', tier);
    localStorage.setItem(this.STORAGE_KEY, tier);
    this.currentTier.set(tier);
  }

  private isValid(value: string | null): value is AgeTier {
    return value === 'primaire' || value === 'college' || value === 'lycee';
  }
}
